/**
 * Zeit kommt nur über diesen Port in die Anwendung (ADR-0030). Die Domäne selbst liest nie die Uhr: Sie bekommt den
 * Zeitpunkt eines Befehls als Wert übergeben.
 */
export interface Uhr {
  jetzt(): Promise<Date>;
}

export const MINUTE_MS = 60_000;
export const STUNDE_MS = 60 * MINUTE_MS;
export const TAG_MS = 24 * STUNDE_MS;

export function plus(t: Date, ms: number): Date {
  return new Date(t.getTime() + ms);
}

const BERLIN = "Europe/Berlin";

function teile(t: Date): Record<string, string> {
  const f = new Intl.DateTimeFormat("de-DE", {
    timeZone: BERLIN, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23",
  });
  return Object.fromEntries(f.formatToParts(t).map((p) => [p.type, p.value]));
}

/** `JJJJ-MM-TT` in Europe/Berlin (README §4.3). */
export function berlinDatum(t: Date): string {
  const p = teile(t);
  return `${p.year}-${p.month}-${p.day}`;
}

/** Beginn (00:00 Europe/Berlin) des Kalendertags `JJJJ-MM-TT`, auch an Tagen mit Zeitumstellung. */
export function berlinTagesbeginn(datum: string): Date {
  const [j, m, d] = datum.split("-").map(Number) as [number, number, number];
  const mitternachtUtc = Date.UTC(j, m - 1, d);
  for (const stunden of [1, 2, 0]) {
    const kandidat = new Date(mitternachtUtc - stunden * STUNDE_MS);
    const p = teile(kandidat);
    if (`${p.year}-${p.month}-${p.day}` === datum && p.hour === "00" && p.minute === "00") return kandidat;
  }
  return new Date(mitternachtUtc);
}

/**
 * Zeitpunkt aus Kalendertag `JJJJ-MM-TT` und Uhrzeit `hh:mm` (auch `h:mm`) in Europe/Berlin, auch an Tagen mit
 * Zeitumstellung; `null`, wenn die Angaben keine gültige Uhrzeit dieses Tages sind.
 */
export function berlinZeitpunktAus(datum: string, uhrzeit: string): Date | null {
  const d = /^(\d{4})-(\d{2})-(\d{2})$/.exec(datum.trim());
  const u = /^(\d{1,2}):(\d{2})$/.exec(uhrzeit.trim());
  if (!d || !u) return null;
  const [j, m, t, h, mi] = [d[1], d[2], d[3], u[1], u[2]].map(Number) as [number, number, number, number, number];
  if (h > 23 || mi > 59) return null;
  const ziel = `${datum.trim()} ${String(h).padStart(2, "0")}:${String(mi).padStart(2, "0")}`;
  for (const stunden of [1, 2, 0]) {
    const kandidat = new Date(Date.UTC(j, m - 1, t, h, mi) - stunden * STUNDE_MS);
    if (berlinZeitpunkt(kandidat) === ziel) return kandidat;
  }
  return null;
}

/** Der Kalendertag nach `JJJJ-MM-TT`. */
export function folgetag(datum: string): string {
  const [j, m, d] = datum.split("-").map(Number) as [number, number, number];
  return new Date(Date.UTC(j, m - 1, d + 1)).toISOString().slice(0, 10);
}

/** `hh:mm` in Europe/Berlin. */
export function berlinUhrzeit(t: Date): string {
  const p = teile(t);
  return `${p.hour}:${p.minute}`;
}

/** `JJJJ-MM-TT hh:mm` in Europe/Berlin (Darstellung von Zeitpunkten in Tabellen, oberflaeche.md §9). */
export function berlinZeitpunkt(t: Date): string {
  return `${berlinDatum(t)} ${berlinUhrzeit(t)}`;
}

/** `TT.MM.JJJJ` (Datum in Fließtext, design/README §11). */
export function berlinDatumLang(t: Date): string {
  const [j, m, d] = berlinDatum(t).split("-");
  return `${d}.${m}.${j}`;
}
